<template>
  <FlexLayout role="region">
    <vue-loading :active="isLoading" :is-full-page="true" />
  </FlexLayout>
</template>

<script>
import VueLoading from "vue-loading-overlay";
import Utils from "@/utils/utils";
import {Login, Mode, Workflow} from "@/constants/constants";
import {useLocaleStore} from "@/stores/locale";
import {useAuthStore} from "@/stores/auth";
import {useAppStore} from "@/stores/app";
import {useOrderStore} from "@/stores/order";
import {useCustomerStore} from "@/stores/customer";
import {useShopStore} from "@/stores/shop";
import orderMixin from "@/components/mixins/orderMixin";
import FlexLayout from "@/components/layout-components/Main/FlexLayout.vue";
import {ssoService} from "@/services/api/ssoService";
import { generateCodeVerifier } from "@/utils/pkceUtils";
import {getAuthenticatedHomeRouteName} from "@/utils/authRouteUtils";

export default {
  components: {
    FlexLayout,
    VueLoading,
  },
  mixins: [orderMixin],
  data() {
    return {
      isLoading: false,
      localJwt: this.jwt, // local copy of jwt
      localShopCode: this.shopCode, // local copy for shopCode if needed
    };
  },
  props: {
    code: String,
    state: String,
    jwt: String,
    shopCode: Number,
    isMealPlan: Boolean,
    isSchool: Boolean,
    validationType: String,
    oneTimeToken: String,
  },
  setup() {
    const localeStore = useLocaleStore();
    const authStore = useAuthStore();
    const appStore = useAppStore();
    const orderStore = useOrderStore();
    const customerStore = useCustomerStore();
    const shopStore = useShopStore();

    return {
      localeStore,
      authStore,
      appStore,
      orderStore,
      customerStore,
      shopStore
    };
  },
  methods: {
    async changeLanguage(newLocale) {
      await this.localeStore.setLocale(newLocale);
    },
    async doNavigationByAppMode() {
      if (this.isSchool) {
        this.$navigateToExternalEntryRoute('StudentAccounts', { withReplace: true });
      } else if (this.localShopCode) {
        this.shopStore.setWondCode(this.localShopCode);
        if (this.isMealPlan) {
          this.$navigateToExternalEntryRoute('MealPlanner', { withReplace: true });
        } else {
          this.$navigateToExternalEntryRoute('OrderMenuPlanner', { withReplace: true });
        }
      } else {
        this.$navigateToExternalEntryRoute(getAuthenticatedHomeRouteName(window.config, this.authStore), { withReplace: true });
      }
    },
    async handleAuthorizationCodeFlow(code, state) {
      this.isLoading = true;
      try {
          // PKCE: retrieve code_verifier from sessionStorage
          const codeVerifier = sessionStorage.getItem("pkce_code_verifier");
          const result = await ssoService.loginWithAuthorizationCode(code, state, codeVerifier);
          if (result.exception !== undefined || result.errorCode < 0) {
            this.$navigateToErrorSSO();
          }

          this.authStore.setAuthenticated(true, Login.Mode.SINGLE_SIGN_ON, Login.UserType.REGISTERED_USER);

          if (result.skipShopSelection) {
            this.$navigateToExternalEntryRoute('OrderMenuPlanner', { withReplace: true });
          } else if (result.goToShopSelection) {
            this.$navigateToExternalEntryRoute('OrderMenuParameters', {
              params: { shopId: result.shop.id },
              withReplace: true,
            });
          } else {
            this.$navigateToExternalEntryRoute(getAuthenticatedHomeRouteName(window.config, this.authStore), { withReplace: true });
          }
      } catch (error) {
        this.$navigateToErrorSSO();
      } finally {
        this.isLoading = false;
      }
    },
    async handleSSO() {
      this.isLoading = true;
      try {
        const result = await ssoService.loginWithJWT(this.localJwt, this.code, this.localShopCode, this.validationType, this.oneTimeToken);
        if (result.exception !== undefined || result.errorCode < 0) {
          this.$navigateToErrorSSO();
          return;
        }

        /*if(result.returnUrl) {
          window.location.href = result.returnUrl;
          return;
        }*/

        this.authStore.setAuthenticated(true, Login.Mode.SINGLE_SIGN_ON, Login.UserType.REGISTERED_USER);

        if (window.config.USE_ALLERGEN_FILTERS && result.wondCustomer.dietaryInfo !== undefined) {
          this.customerStore.setDietaryInfo(result.wondCustomer.dietaryInfo);
        }

        if (window.config.USE_EXTERNAL_LANGUAGE) {
          const newLocale = !Utils.isEmpty(result.customer.locale) ? result.customer.locale : this.config.LANGUAGE_LIST[0].value;
          await this.changeLanguage(newLocale.replace('-', '_'));
        }

        await this.doNavigationByAppMode();
      } catch (error) {
        this.$navigateToErrorSSO();
      } finally {
        this.isLoading = false;
      }
    },
  },
  created() {
    if (this.code) {
      this.handleAuthorizationCodeFlow(this.code, this.state);
      return;
    }

    if (!this.localJwt) {
      // PKCE: generate code_verifier and code_challenge if not present
      if (!sessionStorage.getItem("pkce_code_verifier")) {
        const codeVerifier = generateCodeVerifier();
        sessionStorage.setItem("pkce_code_verifier", codeVerifier);
      }
      const hash = window.location.hash.substring(1); // remove #
      const params = new URLSearchParams(hash);
      console.log("params after hash:", params);

      const idToken = params.get("id_token");
      const shopCode = params.get("s");

      if (idToken) {
        this.localJwt = idToken;
        sessionStorage.setItem("id_token", idToken);
        // Clean the URL so the token isn't visible
        window.history.replaceState({}, document.title, window.location.pathname);
        if (shopCode) {
          this.localShopCode = parseInt(shopCode);
          sessionStorage.setItem("shop_code", shopCode);
        }

        if (this.isSchool) {
          this.appStore.setWorkflow(Workflow.SCHOOL);
        } else if (this.localShopCode) {
          if (this.isMealPlan) {
            this.appStore.setWorkflow(Workflow.MEAL_PLAN);
          } else {
            this.appStore.setWorkflow(Workflow.ORDER);
          }
          this.appStore.setMode(Mode.SHOP);
        }
        this.handleSSO();
      }
    }
  }
};
</script>
